/** The hero swings.
 *
 * The classic gives a hero exactly one verb in a battle: cast. A might hero with an
 * empty spellbook therefore spends a thirty-round siege doing nothing at all -
 * his Attack is in the damage formula and his person is not on the field. H5
 * gave him a blow, and this is that blow, priced into the rules this kernel
 * already has rather than bolted beside them.
 *
 * What it costs is the whole design
 * ---------------------------------
 * It spends the round's hero action (`castThisRound`) and it ends the active
 * stack's turn, which is *exactly* what a spell costs here. So it never adds an
 * action to a round; it fills an empty one. A hero who can cast is choosing
 * between the two, and a hero who cannot is finally choosing between the strike
 * and nothing. That is why there is no cooldown, no charge and no mana on it:
 * the price is already the most expensive thing in the round.
 *
 * What it costs him personally
 * ----------------------------
 * The stack he hits hits back, and the hero has a body to hit.
 *
 * The first version of this had no such price, and it showed: measured over 36
 * battles when the strike shipped, the AI took it in all 36. A verb that is
 * always correct is not a decision, it is an upkeep task. So a struck stack
 * that still has a retaliation left answers - against the hero, not the army -
 * and the hero carries the wound for the rest of the battle. Past his stamina
 * he goes down and the fight ends as a `retreat` for his side: he lives, the
 * army does not.
 *
 * The retaliation counter is the whole of the tactical layer. A stack that has
 * already retaliated this round, one that never retaliates, one that is blind
 * or petrified, cannot answer at all - so "which stack does the hero hit" has
 * an answer, and setting one up is a play.
 *
 * What it is still not
 * --------------------
 * It is not a stack. It provokes no preemptive shot, triggers no on-hit
 * ability and cannot be the thing a Ember Mantle burns; the counterblow below is
 * hand-written and reaches none of that machinery. It carries none of the
 * army's damage modifiers either - Offense, Archery and the artifacts that feed
 * them are `Modifier`s on the *stacks*, and a hero's blow is his own. What is
 * left is the one number he always has: Attack.
 */

import { FP_ONE, roundHalfAway } from "../numeric.js";
import type { Reason } from "../commands.js";
import { Err, Ok, type Result } from "../result.js";
import {
  F_BLINDED, F_PETRIFIED, F_RETALIATED, F_WAR_MACHINE, type BattleState, isAlive,
} from "./state.js";
import { isUntargetable } from "./machines.js";
import { activeStack } from "./order.js";
import {
  DEFENSE_REDUCTION_CAP_FP, DEFENSE_STEP_FP, effectiveDefense,
} from "./damage.js";
import { type Ctx, damageStack } from "./apply.js";
import { jSetField } from "./journal.js";

/**
 * What the blow is worth before the target's armour.
 *
 * Calibrated against the arrow tower, which is the other thing on this
 * battlefield that fires with a hero's Attack and no damage range of its own:
 * the tower is `15 + 2 * attack` once a round for the whole siege, and this is
 * a little more for one round of one battle at the cost of a stack's turn.
 * A 20-Attack hero strikes for 130 before armour, which is a good archer's
 * volley and not a Fireball.
 */
export const HERO_STRIKE_BASE = 30;
export const HERO_STRIKE_PER_ATTACK = 5;

export function heroStrikeDamage(s: BattleState, side: 0 | 1, target: number): number {
  const base = HERO_STRIKE_BASE + HERO_STRIKE_PER_ATTACK * s.heroes[side].attack;
  // The same defence curve every other blow on this field is reduced by, so a
  // well-armoured stack shrugs the hero off exactly as far as it shrugs off an
  // archer. Nothing else in the damage formula applies: see the file header.
  const def = effectiveDefense(s, target);
  const red = Math.min(def * DEFENSE_STEP_FP, DEFENSE_REDUCTION_CAP_FP);
  const dealt = base - roundHalfAway((base * red) / FP_ONE);
  return dealt < 1 ? 1 : dealt;
}

/** Every reason the strike can be refused, in the order a player would ask them. */
export function validateHeroStrike(
  s: BattleState, side: 0 | 1, target: number,
): Result<number, Reason> {
  const hero = s.heroes[side];
  if (hero.id === null) return Err({ code: "TARGET_INVALID" });
  if (hero.castThisRound) return Err({ code: "ALREADY_CAST_THIS_ROUND" });
  // A hero acts during one of his own stacks' turns and that stack pays for it,
  // which is the rule a cast already follows (Section 52).
  const active = activeStack(s);
  if (active < 0 || s.stacks.side[active] !== side) {
    return Err({ code: "STACK_NOT_ACTIVE", stack: active });
  }
  if (!Number.isInteger(target) || target < 0 || target >= s.stacks.n) {
    return Err({ code: "TARGET_INVALID" });
  }
  if (!isAlive(s.stacks, target)) return Err({ code: "STACK_DEAD", stack: target });
  if (s.stacks.side[target] === side) return Err({ code: "TARGET_INVALID" });
  // Section 27 again: a war machine is equipment. Nothing may aim at a breaker,
  // and a hero who could snipe one would make a barred gate unopenable.
  if (isUntargetable(s.stacks, target)) return Err({ code: "TARGET_INVALID" });
  return Ok(active);
}

/**
 * How much punishment a hero can take before he is carried off, and what one
 * counterblow is worth.
 *
 * Stamina is read off Defence because that is the stat that already means "how
 * hard this hero is to hurt" everywhere else in the game, and because it gives
 * a might hero - the one the strike exists for - a real reason to want it. A
 * starting hero with 2 Defence stands up to 160 points, which is about four
 * counterblows from a mid-tier stack; a 20-Defence veteran takes twelve.
 *
 * The counterblow is one creature's top-end swing, not the stack's: the hero
 * leans into the line and pulls back, he does not stand in it. That is what
 * keeps a 500-strong archer stack from ending a hero in one exchange, and it is
 * why the number does not scale with `count` at all.
 */
export const HERO_STAMINA_BASE = 120;
export const HERO_STAMINA_PER_DEFENSE = 20;

export function heroStamina(s: BattleState, side: 0 | 1): number {
  return HERO_STAMINA_BASE + HERO_STAMINA_PER_DEFENSE * s.heroes[side].defense;
}

/**
 * What the stack takes out of the hero, before nothing at all: the hero's own
 * Defence is already in `heroStamina`, so applying the defence curve here as
 * well would charge him for the same armour twice.
 */
export function heroCounterDamage(s: BattleState, striker: number): number {
  const d = s.stacks.damageMax[striker] ?? 0;
  return d < 1 ? 1 : d;
}

/**
 * Can this stack answer the hero?
 *
 * Every clause is one `canRetaliate` already applies to a melee exchange,
 * minus the adjacency test - the hero has no hex to be next to. It is written
 * here rather than reused because `canRetaliate` lives in `execute.ts`, which
 * imports this file: borrowing it would close a cycle, and the four flags it
 * shares are the ones that have to agree.
 */
export function heroCounterAvailable(s: BattleState, striker: number): boolean {
  const st = s.stacks;
  if (!isAlive(st, striker)) return false;
  if ((st.flags[striker]! & (F_BLINDED | F_PETRIFIED | F_WAR_MACHINE)) !== 0) return false;
  if ((st.flags[striker]! & F_RETALIATED) !== 0) return false;
  return true;
}

export interface HeroStrikeResult {
  /** The stack whose turn was spent, for the caller to end. */
  readonly caster: number;
  readonly dealt: number;
  readonly killed: number;
  /** What the hero took answering for it, 0 when nothing could answer. */
  readonly taken: number;
  /** The hero went down: the caller ends the battle as a retreat. */
  readonly downed: boolean;
}

/**
 * Land it. The stack whose turn was spent is read before the blow, because a
 * hero can kill his own active stack (a Ember Mantle rebound is not possible
 * here, but a Clone lapsing inside `checkVictory` is) and a turn machine told
 * to move on from a corpse it never identified would stall.
 */
export function heroStrike(
  c: Ctx, side: 0 | 1, target: number,
): Result<HeroStrikeResult, Reason> {
  const r = validateHeroStrike(c.s, side, target);
  if (!r.ok) return r;
  const caster = r.value;
  const hero = c.s.heroes[side];
  jSetField(c.j, hero as unknown as object, "castThisRound", true);
  const res = damageStack(c, target, heroStrikeDamage(c.s, side, target));
  // Reported the way the moat and the arrow tower report themselves: no stack
  // swung, so attacker and target are the same slot and the renderer draws
  // damage landing rather than a charge. `BattleHeroStruck` is pushed by the
  // caller on top of this, and carries who actually swung.
  c.events.push({
    type: "BattleAttacked", attacker: target, target,
    damage: res.dealt, killed: res.killed, ranged: true, retaliation: false,
  });

  // The answer. Asked after the blow, so a stack the hero killed cannot give
  // one - which is the other half of the decision: a strike that finishes a
  // stack is free, and a strike that wounds one is not.
  let taken = 0;
  let downed = false;
  if (heroCounterAvailable(c.s, target)) {
    taken = heroCounterDamage(c.s, target);
    const hurt = hero.wounds + taken;
    jSetField(c.j, hero as unknown as object, "wounds", hurt);
    c.events.push({
      type: "BattleAttacked", attacker: target, target,
      damage: taken, killed: 0, ranged: false, retaliation: true,
    });
    downed = hurt >= heroStamina(c.s, side);
  }
  return Ok({ caster, dealt: res.dealt, killed: res.killed, taken, downed });
}
